import { cn } from "@postdaily/ui/lib/utils";

import {
  type PlatformId,
  PROVIDER_BRAND,
  ProviderMarkSvg,
  type ProviderVisual,
} from "@/components/social-accounts/provider-icon";
import { SOCIAL_PROFILES } from "@/lib/social-profiles";

/**
 * A network's own mark in its own colour, sized for a light tile.
 *
 * Full colour on a white tile rather than the monochrome glyph `BrandGlyph`
 * draws: every one of these paths is the vendor's complete icon (Facebook's
 * circle, LinkedIn's rounded square, X's and TikTok's solid glyphs), so filling
 * it with the brand colour reproduces the official artwork, while flattening it
 * to a grey silhouette would not.
 *
 * A provider that ships its own multi-colour artwork is drawn from that instead
 * — see the notes in `provider-icon.tsx`. `ProviderMarkSvg` holds it to the
 * 20dp height floor, which is why the flat glyph beside it is also 20px.
 */
function SocialIcon({ platform }: { platform: PlatformId }) {
  // Widened: see the note in provider-icon.tsx about `satisfies`.
  const visual: ProviderVisual = PROVIDER_BRAND[platform];
  if (visual.mark) {
    return <ProviderMarkSvg mark={visual.mark} height={20} />;
  }
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="size-5 shrink-0"
      style={{ fill: visual.bg }}
    >
      <path d={visual.path} />
    </svg>
  );
}

/**
 * The row of links to PostDaily's own accounts. The accounts themselves live in
 * `lib/social-profiles`, which also feeds `sameAs` in the Organization JSON-LD
 * — one list, so a new account cannot appear in the footer but go missing from
 * the structured data.
 */
export function SocialLinks({ className }: { className?: string }) {
  return (
    <ul
      className={cn("flex list-none flex-wrap items-center gap-2", className)}
    >
      {SOCIAL_PROFILES.map((profile) => (
        <li key={profile.platform}>
          <a
            href={profile.url}
            target="_blank"
            // `me` marks these as our own profiles rather than outbound
            // references, which is what verifies the link back from a network
            // that checks (Mastodon, IndieAuth) and reinforces `sameAs`.
            rel="me noreferrer"
            aria-label={`PostDaily on ${profile.network} (${profile.handle})`}
            title={`${profile.network} — ${profile.handle}`}
            // size-10/rounded-[11px] is the contact page's own icon-tile size,
            // so the "Follow along" card lines up with the method cards above
            // it — and a 40px target stays comfortable on a phone, where all
            // five sit on one row at 375px wide.
            className="inline-flex size-10 items-center justify-center rounded-[11px] border border-border bg-card outline-none transition-[transform,box-shadow,border-color] hover:-translate-y-px hover:border-[oklch(0.84_0.04_264)] hover:shadow-[0_8px_18px_-12px_oklch(0.4_0.08_264/0.5)] focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            <SocialIcon platform={profile.platform} />
          </a>
        </li>
      ))}
    </ul>
  );
}
